import { formatEur, type RecurrenceIntervalValue } from "@gladis/shared";
import { Button, Dialog, Label, MoneyInput, SelectField, TextField } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import type { IncomeSourceDto } from "../dto.js";
import { intervalOptions, intervalLabel } from "../intervals.js";

/**
 * What a source has paid, and from when.
 *
 * ==========================================================================================
 * RECORDING AN AMOUNT IS A PRIMARY ACTION HERE, NOT A CORRECTION
 * ==========================================================================================
 *
 * A household adopting GLADIS in 2026 and wanting its chart to start in 2023 enters one source and
 * four dated amounts. That is the supported way to have any history at all, so the form is open on
 * the panel rather than hidden behind an edit affordance, and the date field invites an earlier one
 * rather than warning about it.
 *
 * TWO WAYS TO CHANGE A ROW, AND THEY MEAN DIFFERENT THINGS:
 *
 *   Record the SAME date again  → replaces it. A typo corrected; there is still one answer for that
 *                                 month.
 *   Remove the row              → the date itself was wrong. The months it governed fall back to the
 *                                 row before it.
 *
 * The second exists only because the member chooses the date, so it can be wrong. It is refused for a
 * source's last remaining row, and the server's message names the alternative — end the source if the
 * income stopped, remove the source if it was a mistake.
 */

export interface AmountPeriodDto {
  id: string;
  effectiveFrom: string;
  amountInEuros: number;
  interval: RecurrenceIntervalValue;
}

export interface AmountHistoryProps {
  open: boolean;
  onClose: () => void;
  source: IncomeSourceDto;
  /** Descending by effective date, as the API returns it. */
  periods: readonly AmountPeriodDto[];
  onRecord: (input: { amount: string; interval: string; effectiveFrom: string }) => void;
  onRemove: (periodId: string) => void;
  pending: boolean;
  error?: string | null;
}

export function AmountHistory({
  open,
  onClose,
  source,
  periods,
  onRecord,
  onRemove,
  pending,
  error,
}: AmountHistoryProps): ReactNode {
  const t = useT();
  const [amount, setAmount] = useState("");
  const [interval, setInterval] = useState<string>(source.recurrenceInterval ?? "monthly");
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));

  function submit(event: FormEvent): void {
    event.preventDefault();
    onRecord({ amount, interval, effectiveFrom });
    setAmount("");
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`${source.name} — what it has paid`}
      description={t("inc.history.hint")}
    >
      <div className="space-y-5">
        <form onSubmit={submit} className="grid grid-cols-4 items-end gap-3">
          <div>
            <Label htmlFor="period-amount">{t("inc.source.amount")}</Label>
            <MoneyInput id="period-amount" value={amount} onValueChange={setAmount} required />
          </div>
          <div>
            <Label htmlFor="period-interval">{t("inc.source.howOften")}</Label>
            <SelectField
              id="period-interval"
              value={interval}
              onChange={(event) => setInterval(event.target.value)}
            >
              {intervalOptions().map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
          </div>
          <div>
            <Label htmlFor="period-from">{t("inc.source.from")}</Label>
            <TextField
              id="period-from"
              type="date"
              value={effectiveFrom}
              onChange={(event) => setEffectiveFrom(event.target.value)}
              required
            />
          </div>
          <Button type="submit" disabled={pending}>
            {t("inc.history.record")}
          </Button>
        </form>

        {error ? <p className="text-sm text-danger">{error}</p> : null}

        <ul data-testid="amount-history">
          {periods.map((period) => (
            <li
              key={period.id}
              className="flex items-baseline justify-between gap-3 border-b border-line py-2 last:border-b-0"
            >
              <span className="text-sm text-muted tabular-nums">{period.effectiveFrom}</span>
              <span className="tabular-nums">
                {formatEur(period.amountInEuros)}{" "}
                <span className="text-muted text-sm">
                  {intervalLabel(period.interval).toLowerCase()}
                </span>
              </span>
              <Button
                variant="outline"
                disabled={periods.length <= 1 || pending}
                onClick={() => onRemove(period.id)}
              >
                {t("common.remove")}
              </Button>
            </li>
          ))}
        </ul>

        {periods.length <= 1 ? (
          <p className="text-xs text-muted">{t("inc.history.lastOne")}</p>
        ) : null}
      </div>
    </Dialog>
  );
}
